import { useCallback, useEffect, useState } from 'react';
import type { VerifyItem } from '../../lib/verifyTable';
import { useAppStore } from '../../app/store';
import type { Theme } from '../../data/types';
import { loadSettings } from '../../db/repos/settings';
import { backupStatus, lastBackupLabel, type BackupStatus } from '../../lib/backupReminder';
import { BottomSheet } from '../../ui/BottomSheet';
import { Row, RowList } from '../../ui/Row';
import { SegmentedControl } from '../../ui/SegmentedControl';
import { EmptyState } from '../../ui/States';
import screen from '../screen.module.css';

const THEME_LABEL: Record<Theme, string> = { system: 'System', light: 'Light', dark: 'Dark' };

/** More (not prototyped; DESIGN_SYSTEM §4): grouped rows. */
export function MoreScreen() {
  const theme = useAppStore((s) => s.settings.theme);
  const setTheme = useAppStore((s) => s.setTheme);
  const [themeOpen, setThemeOpen] = useState(false);
  const closeTheme = useCallback(() => setThemeOpen(false), []);
  // The backup reminder (SPEC F8): last backup over 14 days ago and changes since
  const [backup, setBackup] = useState<BackupStatus>();
  const [verifyLine, setVerifyLine] = useState<string>();
  useEffect(() => {
    void loadSettings()
      .then(async (s) => {
        setBackup(backupStatus(s, Date.now()));
        // The checklist data is lazy: it is not in the first download (CLAUDE.md performance)
        const [{ default: items }, { checkedCount, progressLine }] = await Promise.all([import('../../data/generated/verify.json'), import('../../lib/verifyModel')]);
        setVerifyLine(progressLine(checkedCount(items as VerifyItem[], s.verify), items.length));
      })
      .catch(() => undefined);
  }, []);

  return (
    <div className={screen.screen}>
      <h1 className="t-title">More</h1>

      {backup?.due && (
        <RowList label="Reminder">
          <li><Row title="Back up your data" meta={`Last backup: ${lastBackupLabel(backup.daysSince).toLowerCase()}. You've changed things since.`} chevron="accent" to="/more/backup" /></li>
        </RowList>
      )}

      <h2 className={`t-section ${screen.sectionHead}`}>Settings</h2>
      <RowList label="Settings">
        <li>
          <Row title="Theme" trailing={<span className="t-body">{THEME_LABEL[theme]}</span>} chevron onClick={() => setThemeOpen(true)} />
        </li>
      </RowList>

      <h2 className={`t-section ${screen.sectionHead}`}>Data</h2>
      <RowList label="Data">
        <li><Row title="Backup and restore" meta="Back up, restore or reset" chevron to="/more/backup" /></li>
        <li><Row title="Storage" meta="Drum library size and persistence" chevron to="/more/storage" /></li>
      </RowList>

      <h2 className={`t-section ${screen.sectionHead}`}>Verify on MPC</h2>
      <RowList label="Verify on MPC">
        <li><Row title="Checklist" meta={verifyLine ?? 'Checks to run on your MPC'} chevron to="/more/verify" /></li>
      </RowList>

      <h2 className={`t-section ${screen.sectionHead}`}>About</h2>
      <RowList label="About">
        <li><Row title="LowEnd Lab" trailing={<span className="t-body">{__APP_VERSION__}</span>} chevron to="/more/about" /></li>
      </RowList>

      <BottomSheet open={themeOpen} title="Theme" onClose={closeTheme}>
        <div style={{ padding: 'var(--s3) 0' }}>
          <SegmentedControl<Theme>
            label="Theme"
            value={theme}
            onChange={setTheme}
            segments={(['system', 'light', 'dark'] as const).map((t) => ({ value: t, label: THEME_LABEL[t] }))}
          />
        </div>
      </BottomSheet>
    </div>
  );
}

export function MoreSection() {
  return (
    <div className={screen.screen}>
      <h1 className="t-title">Not found</h1>
      <EmptyState message="There's nothing at this address." action={{ label: 'Back to More', to: '/more' }} />
    </div>
  );
}
